import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import axios from "axios";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PreOrderCheckoutPage() {
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    street: "", city: "", postal_code: "", country: "Switzerland",
  });

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    setSubmitting(true);
    try {
      const preorderItems = items.map((i) => ({
        product_id: i.product_id, name: i.name, price: i.price,
        image: i.image, size: i.size, quantity: i.quantity,
      }));
      await axios.post(`${API}/preorders`, { items: preorderItems, shipping: form });
      clearCart();
      toast.success("Pre-order confirmed!", { description: `Confirmation sent to ${form.email}` });
      navigate("/preorder-success");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!items.length) {
    return (
      <main className="pt-28 min-h-screen bg-[#FDFDFD] flex items-center justify-center px-4">
        <SEO title="Pre-Order Checkout" path="/checkout" />
        <div className="text-center">
          <h1 className="text-2xl tracking-tighter text-[#0A0A0A] mb-4">Your pre-order is empty</h1>
          <a href="/shop" className="text-sm text-[#0A0A0A] border-b border-[#0A0A0A] pb-0.5">Browse Products</a>
        </div>
      </main>
    );
  }

  const inputClass = "w-full border border-[#E5E5E5] px-4 py-3 text-sm text-[#0A0A0A] placeholder:text-[#aaa] focus:outline-none focus:border-[#0A0A0A] transition-colors";

  return (
    <main className="bg-[#FDFDFD] min-h-screen">
      <SEO title="Pre-Order Checkout" path="/checkout" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-16 sm:pb-24">
        <h1 className="text-3xl sm:text-4xl tracking-tighter text-[#0A0A0A] mb-2">Pre-Order Checkout</h1>
        <p className="text-sm text-[#888] tracking-wide mb-10" style={{ fontStyle: "normal" }}>
          No payment required now. We'll contact you with payment details before shipping.
        </p>

        <div className="grid lg:grid-cols-[1fr_380px] gap-12 lg:gap-16">
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact */}
            <div>
              <h2 className="text-sm tracking-[0.15em] uppercase text-[#0A0A0A] mb-4" style={{ fontStyle: "normal" }}>Contact</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={form.first_name} onChange={update("first_name")} placeholder="First Name" required
                  data-testid="checkout-first-name" className={inputClass} style={{ fontStyle: "normal" }} />
                <input value={form.last_name} onChange={update("last_name")} placeholder="Last Name" required
                  data-testid="checkout-last-name" className={inputClass} style={{ fontStyle: "normal" }} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <input type="email" value={form.email} onChange={update("email")} placeholder="Email" required
                  data-testid="checkout-email" className={inputClass} style={{ fontStyle: "normal" }} />
                <input type="tel" value={form.phone} onChange={update("phone")} placeholder="Phone (optional)"
                  data-testid="checkout-phone" className={inputClass} style={{ fontStyle: "normal" }} />
              </div>
            </div>

            {/* Shipping */}
            <div>
              <h2 className="text-sm tracking-[0.15em] uppercase text-[#0A0A0A] mb-4" style={{ fontStyle: "normal" }}>Shipping Address</h2>
              <input value={form.street} onChange={update("street")} placeholder="Street Address" required
                data-testid="checkout-street" className={`${inputClass} mb-3`} style={{ fontStyle: "normal" }} />
              <div className="grid sm:grid-cols-3 gap-3">
                <input value={form.postal_code} onChange={update("postal_code")} placeholder="Postal Code" required
                  data-testid="checkout-postal" className={inputClass} style={{ fontStyle: "normal" }} />
                <input value={form.city} onChange={update("city")} placeholder="City" required
                  data-testid="checkout-city" className={inputClass} style={{ fontStyle: "normal" }} />
                <input value={form.country} onChange={update("country")} placeholder="Country" required
                  data-testid="checkout-country" className={inputClass} style={{ fontStyle: "normal" }} />
              </div>
            </div>

            <button type="submit" disabled={submitting} data-testid="place-preorder-button"
              className="w-full bg-[#8B6F47] text-white py-4 text-sm tracking-[0.2em] uppercase hover:bg-[#7A6140] transition-colors active:scale-[0.98] disabled:opacity-50"
              style={{ fontStyle: "normal" }}>
              {submitting ? "Placing Pre-Order..." : "Place Pre-Order"}
            </button>
          </form>

          {/* Order Summary */}
          <div className="lg:border-l lg:border-[#E5E5E5] lg:pl-12">
            <h2 className="text-sm tracking-[0.15em] uppercase text-[#0A0A0A] mb-6" style={{ fontStyle: "normal" }}>Order Summary</h2>
            <div className="space-y-4">
              {items.map((item) => (
                <div key={`${item.product_id}-${item.size}`} className="flex gap-3">
                  <div className="w-16 h-20 bg-[#F3F3F3] overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#0A0A0A] truncate">{item.name}</p>
                    <p className="text-xs text-[#888] mt-0.5" style={{ fontStyle: "normal" }}>Size {item.size} · Qty {item.quantity}</p>
                    <p className="text-sm text-[#0A0A0A] mt-1" style={{ fontStyle: "normal" }}>CHF {(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E5E5E5] mt-6 pt-4 flex justify-between">
              <span className="text-sm tracking-[0.1em] uppercase text-[#888]">Total</span>
              <span className="text-lg text-[#0A0A0A]" style={{ fontStyle: "normal" }}>CHF {totalPrice.toFixed(2)}</span>
            </div>
            <p className="text-[10px] text-[#888] mt-3 tracking-wide" style={{ fontStyle: "normal" }}>
              This is a pre-order. No payment is charged now.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
